import { useState, useEffect } from 'react'

import { TGetItemFn, TRemoveItemFn, TSetItemFn } from '@/config/types'
import { AppCountryTier } from '@/enums/appCountryTier'
import { useContext } from '@/utils/contextUtils'
import { removeCommonItem } from '@/utils/localStorage'

import { ApiService } from '@/api'
import {
  TRegisterUserPayload,
  TReLoginPayload,
  TSendVerificationCodePayload,
  TVerificationPayload,
  TVerificationResendPayload,
  TVerificationV2Payload,
} from '@/api/types'

import { authContext } from './context'
import { contactIdKey, emailKey, tokenHeader, tokenKey, IUser, IAuthContext } from './types'

function getUser(getItem: TGetItemFn): IUser | null {
  const token = getItem(tokenKey)
  const contactId = getItem(contactIdKey)
  if (!token || !contactId) {
    return null
  }
  return {
    contactId,
    token,
  }
}

export function useProvideAuth(
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  removeItem: TRemoveItemFn
): IAuthContext {
  const [user, setUser] = useState<IUser | null>(() => getUser(getItem))

  useEffect(
    () => {
      const token = getItem(tokenKey)
      const contactId = getItem(contactIdKey)
      if (token && contactId) {
        if (token != user?.token) {
          // make request for user
          setUser({
            contactId: contactId || '',
            token: token,
          })
        }
      } else {
        setUser(null)
        // clean old implementation just in case
        removeCommonItem(tokenKey)
        removeCommonItem(contactIdKey)
        removeCommonItem(emailKey)

        removeItem(tokenKey)
        removeItem(contactIdKey)
        // keep email to autofill email input on verification v2
        //removeItem(emailKey)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const authUser = (contactId: string, token: string, email: string | undefined | null): IUser => {
    setItem(contactIdKey, contactId)
    setItem(tokenKey, token)
    setItem(emailKey, email)
    const user = {
      contactId: contactId,
      token,
    }
    setUser(user)
    return user
  }

  const clearUser = () => {
    // clean old implementation just in case
    removeCommonItem(contactIdKey)
    removeCommonItem(tokenKey)
    removeCommonItem(emailKey)

    removeItem(contactIdKey)
    removeItem(tokenKey)
    // keep email to autofill email input on verification v2
    //removeItem(emailKey)
    setUser(null)
  }

  return {
    user,
    signup(payload: TRegisterUserPayload) {
      clearUser()

      return ApiService.registerUser(payload).then(
        (response) => {
          setItem(emailKey, payload.email)
          setItem(contactIdKey, response.data.contact_id)
          setItem('appCountryTier', response.data.app_country_tier || AppCountryTier.T4)

          const token = response.headers[tokenHeader]

          if (token) {
            return {
              contactId: response.data.contact_id,
              state: response.data.state,
              user: authUser(response.data.contact_id, token, payload.email),
              appCountryTier: response.data.app_country_tier || AppCountryTier.T4,
            }
          }
          return {
            contactId: response.data.contact_id,
            state: response.data.state,
            appCountryTier: response.data.app_country_tier || AppCountryTier.T4,
          }
        },
        (error) => {
          return Promise.reject(error)
        }
      )
    },
    login(contactId: string, payload: TVerificationPayload) {
      return ApiService.verifyEmail(contactId, payload).then(
        (response) => {
          const token = response.headers[tokenHeader]
          const email = getItem(emailKey)
          const appCountryTier = response.data.app_country_tier || AppCountryTier.T4
          setItem('appCountryTier', appCountryTier)

          return {
            user: authUser(response.data.contact_id, token, email),
            state: response.data.access_state,
            appCountryTier,
          }
        },
        (error) => {
          return Promise.reject(error)
        }
      )
    },
    loginV2(payload: TVerificationV2Payload) {
      return ApiService.verifyEmailV2(payload).then(
        (response) => {
          const token = response.headers[tokenHeader]
          const email = response.data.email
          const appCountryTier = response.data.app_country_tier || AppCountryTier.T4
          setItem(emailKey, email)
          setItem('appCountryTier', appCountryTier)

          return {
            user: authUser(response.data.contact_id, token, email),
            state: response.data.access_state,
            appCountryTier,
          }
        },
        (error) => {
          return Promise.reject(error)
        }
      )
    },
    relogin(payload: TReLoginPayload) {
      return ApiService.reLogin(payload).then(
        (response) => {
          const appCountryTier = response.data.app_country_tier || AppCountryTier.T4
          setItem('appCountryTier', appCountryTier)

          return {
            state: response.data.state,
            appCountryTier,
          }
        },
        (error) => {
          clearUser()
          return Promise.reject(error)
        }
      )
    },
    resendVerificationCode(contactId: string, payload: TVerificationResendPayload) {
      return ApiService.resendVerificationCode(contactId, payload).then(
        () => {},
        (error) => {
          return Promise.reject(error)
        }
      )
    },
    sendVerificationCode(payload: TSendVerificationCodePayload) {
      return ApiService.sendVerificationCode(payload).then(
        () => {},
        (error) => {
          return Promise.reject(error)
        }
      )
    },
    refreshUser() {
      return new Promise<IUser>((res, rej) => {
        clearUser()
        rej(null)
      })
    },
    logout() {
      return new Promise<void>((res) => {
        clearUser()
        res()
      })
    },
    authUser,
  }
}

export function useAuth(): IAuthContext {
  return useContext(authContext)
}
